import { useMemo } from 'react';

import { useModalController } from '@uikit';

import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useAuthModal,
  useCompleteMagicIdModal,
} from '@shared/common/providers/GlobalModalsProvider';
import { transformReview } from '@shared/helpers/review';

import ReviewsSlider from '@store:web/components/common/ReviewsSlider/ReviewsSlider';

import AddSuggestionModal from './AddSuggestionModal';
import { AppPageSectionProps } from './AppPage';
import { SvgEye } from './AppUserScoreSection';
import ReviewListModal from './ReviewListModal';

const AppSuggestionsSection = ({ app }: AppPageSectionProps) => {
  const { suggestion } = app;
  const { isAuth, user } = useAuth();
  const suggestionModal = useModalController();
  const completeMagicIdModal = useCompleteMagicIdModal();
  const suggestions = useMemo(
    () => suggestion?.map((review) => transformReview({ review })),
    [suggestion],
  );
  const authModal = useAuthModal();

  const handleAddUserReviewButtonClick = () => {
    if (!isAuth) {
      authModal.open();

      return;
    }

    if (!user.attributes.name) {
      completeMagicIdModal.open({
        description: {
          name: null,
        },
        success: false,
      });

      return;
    }

    suggestionModal.open();
  };

  return (
    <section className="sm:app-page-section-card flex flex-col" data-qa="suggestion-block">
      <header className="flex gap-5 justify-between mb-8">
        <h2 className="t-section-title" data-qa="app-suggestions-title">
          {app.attributes.appName} Suggestions by Real Users
        </h2>

        {!!suggestions?.length && (
          <ReviewListModal title="User's Suggestions" reviews={suggestions} app={app} />
        )}
      </header>

      <div className="min-h-[20rem]">
        {!suggestion?.length && (
          <>
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="relative w-full mt-10 mb-7 rounded-full bg-primary-200 h-20">
                <SvgEye className="w-24 h-auto absolute left-0 right-0 top-0 m-auto -translate-y-1/3" />
              </div>

              <p className="font-semibold mb-4">No User's Suggestions to show</p>

              <p className="text-center text-neutral-200 font-medium text-sm">
                You can be the star to make this app better!
                <br />
                Write a suggestion to the developers and let’s build together!
              </p>
            </div>
          </>
        )}
        {!!suggestions?.length && (
          <>
            <p className="text-sm text-neutral-200 mb-8">
              You can be the star to make this app better! Write a suggestion to the developers and
              let’s build together!
            </p>
            <ReviewsSlider
              app={app.attributes}
              id="suggestions-slider"
              reviews={suggestions.slice(0, 6)}
              className="-ml-store-container-px sm:-ml-app-page-section-card-p"
              sliderClassName="pl-store-container-px sm:pl-app-page-section-card-p"
            />
          </>
        )}
      </div>

      <button
        onClick={handleAddUserReviewButtonClick}
        className="px-10 mx-auto button button-solid button-lg flex-grow-0 self-start mt-8"
      >
        + Add Suggestion
      </button>

      <AddSuggestionModal {...suggestionModal.props} app={app} />
    </section>
  );
};

export default AppSuggestionsSection;
